import React from "react"

import { useBlurContext } from "@fuse/sharedComponents/blurWrapper/BlurContext"
import clsx from "clsx"

interface BlurWrapperProps {
  blurIntensityDefault?: "none" | "sm" | "md" | "lg" | "xl" // Tailwind blur classes
  overlayColorDefault?: "light" | "dark" | string // "light" for bg-white, "dark" for bg-black, or any valid Tailwind color class
  overlayOpacityDefault?: string // Tailwind opacity values (e.g., '10', '50', etc.)
  children: React.ReactNode // Content inside the wrapper
  className?: string
  onClick?: (e) => void // Click handler
  disabled?: boolean // Disable interaction
  blurKey?: string // Unique key for each instance
}

export const ensureString = (value: any): string => {
  return typeof value === "string" ? value : String(value)
}

const BlurWrapper: React.FC<BlurWrapperProps> = ({
  blurIntensityDefault = "md",
  overlayColorDefault = "light",
  overlayOpacityDefault = "30",
  children,
  className = "",
  onClick,
  disabled = false,
  blurKey,
}) => {
  const { blurValues } = useBlurContext()

  const blurIntensity =
    blurValues[`${blurKey}-blurIntensity`] || blurIntensityDefault
  const overlayColor =
    blurValues[`${blurKey}-overlayColor`] || overlayColorDefault
  const overlayOpacity = ensureString(
    Number(blurValues[`${blurKey}-overlayOpacity`]) < 0
      ? overlayOpacityDefault // Default value if negative
      : blurValues[`${blurKey}-overlayOpacity`] || overlayOpacityDefault,
  )

  const isInteractive = !!onClick && !disabled

  const resolvedOverlayColor =
    overlayColor === "light"
      ? "bg-white"
      : overlayColor === "dark"
        ? "bg-black"
        : overlayColor

  const wrapperClasses = clsx(
    resolvedOverlayColor,
    `bg-opacity-${overlayOpacity}`,
    `backdrop-blur-${blurIntensity}`,
    {
      "pointer-events-none opacity-50": disabled,
      "cursor-pointer": isInteractive,
    },
    className,
  )

  return (
    <div
      className={wrapperClasses}
      onClick={isInteractive ? onClick : undefined}
    >
      {children}
    </div>
  )
}

export default BlurWrapper
